import type { BoardData } from '../model/schema.js';

/**
 * Search across boards (U51, OIL-25): what of a board matches a query – its name, objects
 * (label, brand / model, notes, tags), channels, mixes, notes and rider texts. Case- and
 * accent-insensitive, and blind to spaces / dashes inside model names ("md421" finds "MD 421");
 * every word of the query must occur in the same field.
 */

export type SearchHitKind = 'board' | 'item' | 'channel' | 'mix' | 'note' | 'rider';

export interface SearchHit {
  kind: SearchHitKind;
  /** Entity id (empty for the board / rider). */
  id: string;
  /** The matching text, shortened around the first word. */
  text: string;
}

/** Hits kept per board. */
export const SEARCH_MAX_HITS = 8;

const fold = (s: string) => s.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase();
/** Folded text without spaces and punctuation. */
const compact = (folded: string) => folded.replace(/[^\p{L}\p{N}]+/gu, '');

/** The query's words (folded); empty when nothing searchable is left. */
export function searchWords(query: string): string[] {
  return fold(query)
    .split(/\s+/)
    .filter((w) => w.length > 0)
    .slice(0, 8);
}

function snippet(text: string, word: string): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length <= 80) return flat;
  const at = Math.max(0, fold(flat).indexOf(word) - 30);
  return `${at > 0 ? '…' : ''}${flat.slice(at, at + 80)}${at + 80 < flat.length ? '…' : ''}`;
}

/** The board's matches for `words` (see searchWords), at most SEARCH_MAX_HITS. */
export function searchBoard(data: BoardData, words: readonly string[]): SearchHit[] {
  if (words.length === 0) return [];
  const hits: SearchHit[] = [];
  // only model-like words (digits, dashes) match across spaces – "md421" ↔ "MD 421", but "kin"
  // must not find "Kick in"
  const tight = words.map((w) => (/\d/.test(w) || compact(w) !== w ? compact(w) : ''));
  const matches = (t: string) => {
    const f = fold(t);
    // the text without separators from each word start on
    const starts = [...f.matchAll(/(?<![\p{L}\p{N}])[\p{L}\p{N}]/gu)].map((m) =>
      compact(f.slice(m.index)),
    );
    return words.every(
      (w, k) => f.includes(w) || (tight[k] !== '' && starts.some((c) => c.startsWith(tight[k]!))),
    );
  };
  /** `show` (optional) is what the hit displays instead of the matching text. */
  const test = (
    kind: SearchHitKind,
    id: string,
    texts: (string | undefined)[],
    show?: (matched: string) => string,
  ) => {
    if (hits.length >= SEARCH_MAX_HITS) return;
    for (const t of texts) {
      if (!t || !matches(t)) continue;
      const text = show ? show(t) : t;
      hits.push({ kind, id, text: snippet(text, words[0]!) });
      return;
    }
  };
  test('board', '', [data.meta.name]);
  for (const i of Object.values(data.items)) {
    const device = [i.brand, i.model].filter(Boolean).join(' ');
    // a hit in brand / model, notes or tags still says which object it is
    test('item', i.id, [i.label, device, `${i.label} ${device}`, i.notes, i.tags.join(' ')], (t) =>
      i.label && t !== i.label && !t.startsWith(`${i.label} `) ? `${i.label} · ${t}` : t,
    );
  }
  for (const c of Object.values(data.channels))
    test('channel', c.id, [`${c.number} ${c.name}`, c.sourceText, c.notes]);
  for (const m of Object.values(data.mixes)) test('mix', m.id, [`${m.number} ${m.name}`, m.notes]);
  for (const f of Object.values(data.freeform)) test('note', f.id, [f.text]);
  const r = data.meta.rider;
  if (r)
    test('rider', '', [
      r.artist,
      r.tour,
      r.venue,
      ...(r.contacts ?? []).map((c) => `${c.role} ${c.name}`),
      ...(r.sections ?? []).flatMap((s) => [s.title, s.body]),
    ]);
  return hits;
}
